import { useState } from "react";
import {
  RiUserLine,
  RiFileTextLine,
  RiWalletLine,
  RiSettings4Line,
  RiNotificationLine,
  RiLockLine,
  RiArrowLeftLine,
  RiEditLine,
  RiDownloadLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [customerData] = useState({
    name: "John Doe",
    email: "john@example.com",
    company: "Tech Solutions Inc",
    phone: "+1 234 567 890",
    address: "123 Business Street, City, State 12345",
    paymentMethods: [
      {
        id: 1,
        type: "Credit Card",
        last4: "4242",
        expiry: "12/24",
        isDefault: true,
      },
      {
        id: 2,
        type: "PayPal",
        email: "john@example.com",
        isDefault: false,
      },
    ],
    recentInvoices: [
      {
        id: "INV-2024-001",
        amount: 2499.99,
        date: "2024-02-20",
        status: "Paid",
      },
      {
        id: "INV-2024-002",
        amount: 1899.99,
        date: "2024-02-25",
        status: "Pending",
      },
    ],
    notifications: {
      invoiceReminders: true,
      paymentConfirmations: true,
      accountUpdates: false,
    },
  });

  const tabs = [
    {
      id: "overview",
      name: "Overview",
      icon: RiUserLine,
    },
    {
      id: "invoices",
      name: "Invoices",
      icon: RiFileTextLine,
    },
    {
      id: "payments",
      name: "Payment Methods",
      icon: RiWalletLine,
    },
    {
      id: "notifications",
      name: "Notifications",
      icon: RiNotificationLine,
    },
    {
      id: "settings",
      name: "Account Settings",
      icon: RiSettings4Line,
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Account Overview</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-600">
                    Personal Details
                  </h3>
                  <div className="mt-2 space-y-2">
                    <p>{customerData.name}</p>
                    <p>{customerData.email}</p>
                    <p>{customerData.phone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-600">Company Details</h3>
                  <div className="mt-2 space-y-2">
                    <p>{customerData.company}</p>
                    <p className="whitespace-pre-line">
                      {customerData.address}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
              <div className="space-y-4">
                {customerData.recentInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div>
                      <h3 className="font-medium">{invoice.id}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(invoice.date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${invoice.amount}</p>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.status === "Paid"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "payments":
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Payment Methods</h2>
              <button className="btn btn-primary">Add Payment Method</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customerData.paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className="card flex justify-between items-center"
                >
                  <div className="flex items-center gap-4">
                    {method.type === "Credit Card" ? (
                      <RiWalletLine className="w-8 h-8 text-primary-600" />
                    ) : (
                      <RiWalletLine className="w-8 h-8 text-blue-600" />
                    )}
                    <div>
                      <h3 className="font-medium">
                        {method.type}{" "}
                        {method.isDefault && (
                          <span className="text-xs text-primary-600">
                            (Default)
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {method.type === "Credit Card"
                          ? `**** ${method.last4} • Expires ${method.expiry}`
                          : method.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-icon">
                      <RiEditLine className="w-5 h-5" />
                    </button>
                    {!method.isDefault && (
                      <button className="btn btn-icon text-red-600">
                        <RiLockLine className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "notifications":
        return (
          <div className="card">
            <h2 className="text-xl font-semibold mb-6">
              Notification Settings
            </h2>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Invoice Reminders</h3>
                  <p className="text-sm text-gray-500">
                    Get notified when invoices are due
                  </p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={customerData.notifications.invoiceReminders}
                    onChange={() => {}}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Payment Confirmations</h3>
                  <p className="text-sm text-gray-500">
                    Receive payment confirmation emails
                  </p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={customerData.notifications.paymentConfirmations}
                    onChange={() => {}}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Account Updates</h3>
                  <p className="text-sm text-gray-500">
                    Get notified about account changes
                  </p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={customerData.notifications.accountUpdates}
                    onChange={() => {}}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/billing")}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          >
            <RiArrowLeftLine className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Customer Portal</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`flex items-center gap-2 w-full p-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-primary-50 text-primary-600"
                      : "hover:bg-gray-50 text-gray-600"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default CustomerPortal;
